import React, { useEffect, useState } from "react";
import {
  User,
  Mail,
  Shield,
  Edit,
  Trash2,
  Plus,
  Building2,
  ShoppingCart,
} from "lucide-react";

const ROLE_LABELS = {
  utente: "operatore",
  admin: "admin",
  superadmin: "superadmin",
};

function Modal({ open, onClose, children, title }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="font-semibold flex items-center gap-2">
            <Shield size={16} /> {title}
          </h2>
          <button onClick={onClose} className="text-slate-500 text-sm">
            x
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function quotaText(bucket) {
  if (!bucket) return "-";
  if (bucket.unlimited) return `${bucket.count} / illimitati`;
  return `${bucket.count} / ${bucket.limit}`;
}

export default function UtentiRuoli({ isSuper }) {
  const [utenti, setUtenti] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const canManageUsers = true;

  const adminLimitReached =
    !isSuper &&
    quota?.admins &&
    !quota.admins.unlimited &&
    quota.admins.count >= quota.admins.limit;

  const operatorLimitReached =
    !isSuper &&
    quota?.operatori &&
    !quota.operatori.unlimited &&
    quota.operatori.count >= quota.operatori.limit;

  const canCreateAdmin = isSuper || !adminLimitReached;
  const canCreateOperator = isSuper || !operatorLimitReached;
  const canCreateAnyUser = isSuper || canCreateAdmin || canCreateOperator;
  const showBuyOperator =
    !isSuper &&
    !canCreateAnyUser &&
    quota?.operatori &&
    !quota.operatori.unlimited;

  let roleOptions = [];
  if (editUser) {
    const isEdit = Boolean(editUser.id);

    if (isSuper) {
      roleOptions = [
        { value: "utente", label: "operatore" },
        { value: "admin", label: "admin" },
        { value: "superadmin", label: "superadmin" },
      ];
    } else {
      if (canCreateOperator || (isEdit && editUser.ruolo === "utente")) {
        roleOptions.push({ value: "utente", label: "operatore" });
      }

      if (canCreateAdmin || (isEdit && editUser.ruolo === "admin")) {
        roleOptions.push({ value: "admin", label: "admin" });
      }
    }
  }

  const loadUtenti = () => {
    setLoading(true);
    setErrore("");

    fetch("/api/admin/utenti_list.php", { credentials: "include" })
      .then(async (r) => {
        const data = await r.json().catch(() => null);
        if (!r.ok || data?.ok === false) {
          throw new Error(data?.error || "Errore caricamento utenti");
        }
        return data;
      })
      .then((data) => {
        const users = Array.isArray(data) ? data : data?.utenti;
        setUtenti(Array.isArray(users) ? users : []);
        setQuota(data?.quota || null);
        setLoading(false);
      })
      .catch((err) => {
        setErrore("Impossibile caricare gli utenti: " + err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!canManageUsers) return;
    loadUtenti();
  }, [canManageUsers]);

  const apriNuovo = () => {
    if (!canCreateAnyUser) return;

    const initialRole = canCreateOperator ? "utente" : "admin";
    setEditUser({
      id: 0,
      nome: "",
      email: "",
      ruolo: initialRole,
      azienda_id: "",
      password: "",
    });
    setModalOpen(true);
  };

  const apriModifica = (u) => {
    setEditUser({
      id: u.id,
      nome: u.nome || "",
      email: u.email || "",
      ruolo: u.ruolo || "utente",
      azienda_id: u.azienda_id || "",
      password: "",
    });
    setModalOpen(true);
  };

  const compraOperatore = () => {
    window.alert("Checkout esterno non ancora attivo.");
  };

  const salva = () => {
    if (!editUser) return;

    const payload = { ...editUser };

    if (!payload.password) delete payload.password;

    if (!isSuper) {
      delete payload.azienda_id;
      if (payload.ruolo === "superadmin") {
        payload.ruolo = "utente";
      }
    }

    fetch("/api/admin/utente_save.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => null);
        if (!r.ok || data?.ok === false) {
          throw data || new Error("Errore salvataggio");
        }
        return data;
      })
      .then(() => {
        setModalOpen(false);
        setEditUser(null);
        loadUtenti();
      })
      .catch((err) => {
        const errorCode = err?.error || err?.message || "Errore salvataggio";

        if (errorCode === "USER_ROLE_LIMIT_REACHED") {
          const roleLabel = err?.role === "admin" ? "admin" : "operatori";
          const planLabel = err?.plan_label || "piano attivo";
          alert(
            `Limite ${roleLabel} raggiunto per ${planLabel}.` +
            (err?.role === "utente"
              ? " Usa il pulsante Compra operatore per la futura estensione."
              : "")
          );
          loadUtenti();
          return;
        }

        alert("Errore salvataggio: " + errorCode);
      });
  };

  const elimina = (u) => {
    if (!window.confirm("Eliminare l'utente " + u.nome + "?")) return;

    fetch("/api/admin/utente_delete.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: u.id }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => null);
        if (!r.ok || data?.ok === false) {
          throw new Error(data?.error || "Errore eliminazione");
        }
        return data;
      })
      .then(() => loadUtenti())
      .catch((err) => alert("Errore eliminazione: " + err.message));
  };

  if (!canManageUsers) return null;
  if (loading) return <div>Caricamento utenti...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <User size={18} /> Utenti
          </h2>
          <p className="text-sm text-slate-500">
            Gestisci accessi, ruolo e azienda collegata.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {showBuyOperator && (
            <button
              onClick={compraOperatore}
              className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"
            >
              <ShoppingCart size={14} /> Compra operatore
            </button>
          )}

          <button
            onClick={apriNuovo}
            disabled={!canCreateAnyUser}
            className={
              canCreateAnyUser
                ? "bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"
                : "bg-slate-200 text-slate-500 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 cursor-not-allowed"
            }
          >
            <Plus size={14} /> Nuovo utente
          </button>
        </div>
      </div>

      {quota && (
        <div className="grid md:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              Piano attivo
            </div>
            <div className="text-base font-semibold text-slate-900">
              {quota.plan_label || quota.plan_code || "-"}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              Admin
            </div>
            <div className="text-base font-semibold text-slate-900">
              {quotaText(quota.admins)}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              Operatori
            </div>
            <div className="text-base font-semibold text-slate-900">
              {quotaText(quota.operatori)}
            </div>
          </div>
        </div>
      )}

      {showBuyOperator && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-xl text-sm">
          Hai raggiunto il numero massimo di operatori previsto dal piano corrente.
        </div>
      )}

      {errore && (
        <div className="bg-amber-100 text-amber-900 px-3 py-2 rounded">
          {errore}
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {utenti.map((u) => (
          <div
            key={u.id}
            className="bg-white rounded-xl border border-slate-100 shadow-sm p-3 flex flex-col gap-2"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex gap-2 min-w-0">
                <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <User size={16} className="text-slate-500" />
                </div>

                <div className="min-w-0">
                  <div className="font-semibold truncate">{u.nome}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 truncate">
                    <Mail size={12} className="shrink-0" />
                    <span className="truncate">{u.email}</span>
                  </div>
                </div>
              </div>

              <span
                className={
                  u.ruolo === "superadmin"
                    ? "px-2 py-0.5 rounded-full text-xs bg-fuchsia-100 text-fuchsia-700 flex items-center gap-1 shrink-0"
                    : u.ruolo === "admin"
                      ? "px-2 py-0.5 rounded-full text-xs bg-sky-100 text-sky-700 flex items-center gap-1 shrink-0"
                      : "px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600 flex items-center gap-1 shrink-0"
                }
              >
                <Shield size={12} />
                {ROLE_LABELS[u.ruolo] || u.ruolo}
              </span>
            </div>

            <div className="text-xs text-slate-500 flex items-center gap-1">
              <Building2 size={12} />
              Azienda: {u.azienda || u.azienda_id || "-"}
            </div>

            <div className="flex gap-2 mt-1 flex-wrap">
              <button
                onClick={() => apriModifica(u)}
                className="text-xs bg-slate-900 text-white px-2 py-1 rounded flex items-center gap-1"
              >
                <Edit size={12} /> Modifica
              </button>

              <button
                onClick={() => elimina(u)}
                className="text-xs bg-rose-100 text-rose-800 px-2 py-1 rounded flex items-center gap-1"
              >
                <Trash2 size={12} /> Elimina
              </button>
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditUser(null);
        }}
        title={editUser?.id ? "Modifica utente" : "Nuovo utente"}
      >
        {editUser && (
          <div className="space-y-3">
            <div>
              <label className="text-sm">Nome</label>
              <input
                value={editUser.nome}
                onChange={(e) =>
                  setEditUser({ ...editUser, nome: e.target.value })
                }
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>

            <div>
              <label className="text-sm">Email</label>
              <input
                type="email"
                value={editUser.email}
                onChange={(e) =>
                  setEditUser({ ...editUser, email: e.target.value })
                }
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>

            <div>
              <label className="text-sm">Ruolo</label>
              <select
                value={editUser.ruolo}
                onChange={(e) =>
                  setEditUser({ ...editUser, ruolo: e.target.value })
                }
                className="w-full border rounded px-2 py-1 text-sm"
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {isSuper ? (
              <div>
                <label className="text-sm">ID Azienda</label>
                <input
                  value={editUser.azienda_id || ""}
                  onChange={(e) =>
                    setEditUser({ ...editUser, azienda_id: e.target.value })
                  }
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
            ) : (
              <div>
                <label className="text-sm">Azienda</label>
                <div className="w-full border rounded px-2 py-2 text-sm bg-slate-50 text-slate-500">
                  L'utente verra' associato automaticamente alla tua azienda
                </div>
              </div>
            )}

            <div>
              <label className="text-sm">
                Password {editUser.id ? "(vuoto = non cambiare)" : ""}
              </label>
              <input
                type="password"
                value={editUser.password}
                onChange={(e) =>
                  setEditUser({ ...editUser, password: e.target.value })
                }
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => {
                  setModalOpen(false);
                  setEditUser(null);
                }}
                className="px-3 py-1.5 text-sm rounded bg-slate-100"
              >
                Annulla
              </button>
              <button
                onClick={salva}
                className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white"
              >
                Salva
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
